import React, { useContext, useState, useCallback, useRef } from 'react';
import { Grid } from '@material-ui/core';
import { css } from '@emotion/react';
import { compose } from 'recompose';
import { colors } from '@/styles';
import { useClientUser } from '@/hooks';
import { Context, ContextProvider } from './store';
import { PageTitle } from './blocks';
import SubregionList from './SubregionList';
import AssignToSubregionPopover from './AssignToSubregionPopover';
import Snackbar from '../Snackbar';
import { OnwardSwitch } from '@/styles/blocks';
import SubregionMap from './SubregionMap';
import SubregionPanel from './panel/SubregionPanel';
import DiscardChangesModal from './panel/DiscardChangesModal';
import { usePanelSubject, useSubregionDraft, useSyncPanelDirty } from './panel/hooks';
import RegionSettings from './RegionSettings';

const SubRegions = () => {
    const mapRef = useRef(null);
    const [root, setRoot] = useState(null);
    const { locations } = useClientUser();
    const { callbacks, state, loading } = useContext(Context);
    const [highlightOverride, setHighlightOverride] = useState(false);

    const initRoot = useCallback((node) => {
        if (node !== null) {
            setRoot(node);
        }
    }, []);

    const panel = usePanelSubject({
        selectedSubregionId: state.selectedSubregion?.subregion_id || null,
        callbacks,
    });
    const panelOpen = panel.creating || !!state.selectedSubregion;
    const draft = useSubregionDraft({
        subregion: state.selectedSubregion || null,
        isNew: panel.creating,
        callbacks,
        onSaved: (saved) => {
            panel.request({ kind: 'saved', id: saved?.subregion_id || null });
            callbacks.setNotification({ severity: 'success', message: 'Subregion saved' });
        },
    });

    useSyncPanelDirty(panel.setDirty, panelOpen && draft.isDirty);

    return (
        <Grid
            container
            direction="column"
            css={css`
                height: 100%;
                background: ${colors.white.primary};
                flex-wrap: nowrap;
            `}
        >
            <Snackbar
                open={!!state.notification.message}
                {...state.notification}
                handleClose={() => callbacks.setNotification({})}
            />
            <DiscardChangesModal {...panel.discardPrompt} />
            <Grid
                container
                direction="row"
                css={css`
                    padding: 23px 45px;
                    border-bottom: 1px solid ${colors.greys[4]};
                    flex: 0;
                    flex-basis: 0;
                    align-items: center;
                    justify-content: space-between;
                `}
            >
                <PageTitle>Regions</PageTitle>
                <OnwardSwitch
                    label={'Highlight All'}
                    checked={highlightOverride}
                    onChange={() => setHighlightOverride((prev) => !prev)}
                />
            </Grid>

            <Grid
                container
                direction="row"
                css={css`
                    flex-basis: 0;
                    flex: 1;
                    min-height: 0;
                    flex-wrap: nowrap;
                `}
            >
                <Grid
                    item
                    container
                    direction="column"
                    css={css`
                        flex: 0;
                        flex-basis: 0;
                        flex-wrap: nowrap;
                        min-width: 360px;
                        max-width: 360px;
                        /* Scroll instead of stretching the row (and the map). */
                        min-height: 0;
                        overflow-y: auto;
                        border-right: 1px solid ${colors.greys[0]};
                    `}
                >
                    <SubregionList
                        options={{
                            enableAddSubregions: true,
                            enableImport: true,
                            enableExport: true,
                        }}
                        loading={loading}
                        filter={state.filter}
                        selected={state.selectedSubregion}
                        subregions={state.subregions}
                        callbacks={{
                            ...callbacks,
                            onAddSubregion: () => panel.request({ kind: 'new' }),
                            toggleSubregion: (id) => panel.request({ kind: 'select', id }),
                        }}
                    />
                </Grid>
                <Grid
                    item
                    container
                    direction="column"
                    css={css`
                        flex: 1;
                        flex-basis: 0;
                        overflow: hidden;
                        position: relative;
                    `}
                >
                    <div
                        ref={initRoot}
                        css={css`
                            width: 100%;
                            height: 100%;
                            display: flex;
                        `}
                    />
                    <SubregionMap
                        ref={mapRef}
                        node={root}
                        subregions={state.subregions}
                        callbacks={{
                            ...callbacks,
                            onLoad: (map) => {
                                const [long, lat] = locations.reduce((acc, location) => {
                                    if (location.lng && location.lat) {
                                        acc = [location.lng, location.lat];
                                    }

                                    return acc;
                                }, []);

                                if (long && lat) {
                                    map.jumpTo({
                                        center: [long, lat],
                                    });
                                }
                            },
                        }}
                        highlightOverride={highlightOverride}
                        selectedSubregion={state.selectedSubregion}
                        hoveredSubregion={state.hoveredSubregion}
                        selectedZips={state.selectedZips}
                        hoveredSubregionZip={state.hoveredSubregionZip}
                        zipToggle={panelOpen ? { zips: draft.zips, onToggle: draft.toggle } : null}
                    />
                    {panelOpen ? (
                        <SubregionPanel
                            subregion={state.selectedSubregion || null}
                            subregions={state.subregions}
                            callbacks={callbacks}
                            draft={draft}
                            onClose={() => panel.request({ kind: 'close' })}
                            settings={
                                <RegionSettings
                                    values={draft.settings}
                                    onChange={draft.setSetting}
                                    locations={locations}
                                    disabled={draft.saving}
                                />
                            }
                        />
                    ) : null}

                    {state.selectedZips.length > 0 ? (
                        <AssignToSubregionPopover
                            loading={loading}
                            subregions={state.subregions}
                            selectedZips={state.selectedZips}
                            callbacks={callbacks}
                        />
                    ) : null}
                </Grid>
            </Grid>
        </Grid>
    );
};

const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} />
        </ContextProvider>
    );

export default compose(withContext)(SubRegions);
